/** Renderer-independent paint, in normalized object coordinates. */
export interface GradientStop {
  offset: number;
  color: string;
  alpha: number;
}
export interface GradientPaint {
  type: "linear" | "radial";
  angle: number;
  stops: GradientStop[];
  coordinates?: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    r1: number;
    r2: number;
  };
}
export function isGradient(value: unknown): value is GradientPaint {
  if (!value || typeof value !== "object") return false;
  const g = value as GradientPaint;
  return (
    (g.type === "linear" || g.type === "radial") &&
    Number.isFinite(g.angle) &&
    g.angle >= 0 &&
    g.angle <= 360 &&
    (g.coordinates === undefined ||
      (!!g.coordinates &&
        [
          g.coordinates.x1,
          g.coordinates.y1,
          g.coordinates.x2,
          g.coordinates.y2,
          g.coordinates.r1,
          g.coordinates.r2,
        ].every((n) => Number.isFinite(n) && Math.abs(n) <= 1e6) &&
        g.coordinates.r1 >= 0 &&
        g.coordinates.r2 >= 0)) &&
    Array.isArray(g.stops) &&
    g.stops.length >= 2 &&
    g.stops.length <= 16 &&
    g.stops.every(
      (s, i) =>
        s &&
        Number.isFinite(s.offset) &&
        s.offset >= 0 &&
        s.offset <= 1 &&
        (!i || s.offset >= g.stops[i - 1]!.offset) &&
        /^#[a-f\d]{6}$/i.test(s.color) &&
        Number.isFinite(s.alpha) &&
        s.alpha >= 0 &&
        s.alpha <= 1,
    )
  );
}
export function assertGradient(value: unknown) {
  if (value !== undefined && value !== null && !isGradient(value))
    throw new Error("Повреждены настройки градиента.");
}
export function defaultGradient(color: string): GradientPaint {
  return {
    type: "linear",
    angle: 0,
    stops: [
      { offset: 0, color, alpha: 1 },
      { offset: 1, color: "#ffffff", alpha: 1 },
    ],
  };
}
export function stopColor(stop: GradientStop) {
  return (
    stop.color +
    Math.round(stop.alpha * 255)
      .toString(16)
      .padStart(2, "0")
  );
}
export function gradientCss(g: GradientPaint) {
  const stops = g.stops
    .map((s) => `${stopColor(s)} ${s.offset * 100}%`)
    .join(",");
  return g.type === "linear"
    ? `linear-gradient(${g.angle + 90}deg,${stops})`
    : `radial-gradient(circle closest-side,${stops})`;
}
